// src/pages/nfl/NflStacks.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
} from "recharts";

const NFL_BASE = "/data/nfl/latest";
const CLASSIC_BASE = "/data/nfl/classic/latest";

const SOURCES = {
  vegas: [`${NFL_BASE}/vegas.json`, `${CLASSIC_BASE}/vegas.json`],
  projections: [`${NFL_BASE}/projections.json`, `${CLASSIC_BASE}/projections.json`],
  siteIds: [`${NFL_BASE}/site_ids.json`, `${CLASSIC_BASE}/site_ids.json`],
  playerSim: `${NFL_BASE}/player_sim_results.json`,
  qb1: `${NFL_BASE}/passing_stack_results.json`,
  qb1Team: `${NFL_BASE}/passing_stack_team_results.json`,
  qb2: `${NFL_BASE}/passing_stack_qb_plus_2_results.json`,
  qb2Team: `${NFL_BASE}/passing_stack_qb_plus_2_team_results.json`,
  qb3: `${NFL_BASE}/passing_stack_qb_plus_3_results.json`,
  qb3Team: `${NFL_BASE}/passing_stack_qb_plus_3_team_results.json`,
  meta: `${NFL_BASE}/meta.json`,
};

const STACK_OPTIONS = [
  { key: "qb1", label: "QB + 1", teamKey: "qb1Team" },
  { key: "qb2", label: "QB + 2", teamKey: "qb2Team" },
  { key: "qb3", label: "QB + 3", teamKey: "qb3Team" },
];

const SITE_OPTIONS = [
  { key: "dk", label: "DK", logo: "/logos/dk.png" },
  { key: "fd", label: "FD", logo: "/logos/fd.png" },
  { key: "both", label: "Both" },
];

const SLATE_OPTIONS = [
  { key: "all", label: "All" },
  { key: "early", label: "Early" },
  { key: "main", label: "Main" },
  { key: "late", label: "Late" },
];

const POSITIONS = ["QB", "RB", "WR", "TE", "DST"];

const teamLogo = (team) =>
  `/logos/nfl/${String(team || "").toUpperCase()}.png`;

const n = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const x = Number(v);
  return Number.isFinite(x) ? x : null;
};

const pctFraction = (v) => {
  const x = n(v);
  return x == null ? null : x * 100;
};

const pctPointValue = (v) => {
  const x = n(v);
  return x == null ? null : x;
};

const fmt = {
  num(v, d = 1) {
    const x = n(v);
    return x == null ? "—" : x.toFixed(d);
  },

  int(v) {
    const x = n(v);
    return x == null ? "—" : Math.round(x).toLocaleString();
  },

  money(v) {
    const x = n(v);
    return x == null ? "—" : `$${Math.round(x).toLocaleString()}`;
  },

  pctFraction(v, d = 1) {
    const x = pctFraction(v);
    return x == null ? "—" : `${x.toFixed(d)}%`;
  },

  pctPoints(v, d = 1) {
    const x = pctPointValue(v);
    return x == null ? "—" : `${x.toFixed(d)}%`;
  },

  signed(v) {
    const x = n(v);
    if (x == null) return "—";
    const body = Number.isInteger(x) ? String(x) : x.toFixed(1);
    return `${x > 0 ? "+" : ""}${body}`;
  },
};

const normTeam = (v) => String(v || "").trim().toUpperCase();

const normPos = (v) => {
  const p = String(v || "").trim().toUpperCase();
  return ["DEF", "D/ST", "D"].includes(p) ? "DST" : p;
};

const normName = (v) =>
  String(v || "")
    .toLowerCase()
    .replace(/[.'’]/g, "")
    .replace(/\b(jr|sr|ii|iii|iv)\b/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const playerKey = (team, player) =>
  `${normTeam(team)}|${normName(player)}`;

const hasId = (v) =>
  v !== null && v !== undefined && String(v).trim() !== "";

function buildSitePlayerIndex(raw) {
  const out = {
    dk: new Map(),
    fd: new Map(),
  };

  if (!raw || typeof raw !== "object") return out;

  for (const site of ["dk", "fd"]) {
    const rows = Array.isArray(raw?.[site]) ? raw[site] : [];

    for (const row of rows) {
      const team = normTeam(row?.team);
      const name = normName(row?.name);

      if (!team || !name) continue;

      out[site].set(`${team}|${name}`, row);
    }
  }

  return out;
}

function getSlatePlayerInfo(siteIndex, site, team, player, slate = "all") {
  const row = siteIndex?.[site]?.get(playerKey(team, player));
  if (!row) return null;

  const get = (idKey, salaryKey) => {
    const salary = n(row?.[salaryKey]);

    if (!hasId(row?.[idKey]) || salary == null || salary <= 0) {
      return null;
    }

    return {
      id: row[idKey],
      salary,
      row,
    };
  };

  const main = get("id", "salary");
  const early = get("early_id", "early_salary");
  const late = get("late_id", "late_salary");

  if (slate === "main") return main;
  if (slate === "early") return early;
  if (slate === "late") return late;

  return main || early || late;
}

function normalizePctPoints(rows, rawKey, eligibleKey, outKey) {
  const total = rows.reduce((acc, row) => {
    if (!row?.[eligibleKey]) return acc;

    const v = n(row?.[rawKey]);
    return acc + (v != null && v > 0 ? v : 0);
  }, 0);

  return rows.map((row) => ({
    ...row,
    [outKey]:
      row?.[eligibleKey] &&
      total > 0 &&
      (n(row?.[rawKey]) || 0) > 0
        ? ((n(row[rawKey]) || 0) / total) * 100
        : null,
  }));
}

const sum = (vals) =>
  vals
    .map(n)
    .filter((x) => x != null)
    .reduce((a, b) => a + b, 0);

const mean = (vals) => {
  const a = vals.map(n).filter((x) => x != null);

  return a.length
    ? a.reduce((x, y) => x + y, 0) / a.length
    : null;
};

async function fetchOneJson(key, url) {
  const res = await fetch(`${url}?_=${Date.now()}`, {
    cache: "no-store",
  });

  const text = await res.text();
  const trimmed = text.trim();

  if (!res.ok) {
    throw new Error(`${key}: HTTP ${res.status} loading ${url}`);
  }

  if (
    !trimmed ||
    trimmed.startsWith("<!DOCTYPE") ||
    trimmed.startsWith("<!doctype") ||
    trimmed.startsWith("<html")
  ) {
    throw new Error(`${key}: ${url} returned HTML instead of JSON`);
  }

  try {
    return JSON.parse(text);
  } catch (e) {
    throw new Error(
      `${key}: invalid JSON at ${url} — ${e.message}`
    );
  }
}

async function fetchJsonSafe(key, source, { optional = false } = {}) {
  const urls = Array.isArray(source) ? source : [source];
  const errors = [];

  for (const url of urls) {
    try {
      return await fetchOneJson(key, url);
    } catch (e) {
      errors.push(e.message);
    }
  }

  if (optional) return null;

  throw new Error(
    `${key}: could not load JSON. ${errors.join(" | ")}`
  );
}

function useStackData() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      setErr("");

      try {
        const [vegas, projections, siteIds] = await Promise.all([
          fetchJsonSafe("vegas", SOURCES.vegas),
          fetchJsonSafe("projections", SOURCES.projections),
          fetchJsonSafe("siteIds", SOURCES.siteIds),
        ]);

        const keys = [
          "qb1",
          "qb1Team",
          "qb2",
          "qb2Team",
          "qb3",
          "qb3Team",
        ];

        const stackData = await Promise.all(
          keys.map(async (key) => {
            try {
              return [
                key,
                await fetchJsonSafe(key, SOURCES[key]),
              ];
            } catch (e) {
              console.warn(e.message);
              return [key, []];
            }
          })
        );

        const [playerSim, meta] = await Promise.all([
          fetchJsonSafe("playerSim", SOURCES.playerSim, {
            optional: true,
          }),
          fetchJsonSafe("meta", SOURCES.meta, {
            optional: true,
          }),
        ]);

        if (alive) {
          setData({
            vegas: Array.isArray(vegas) ? vegas : [],
            projections: Array.isArray(projections)
              ? projections
              : [],
            siteIds:
              siteIds && typeof siteIds === "object"
                ? siteIds
                : { dk: [], fd: [] },
            playerSim: Array.isArray(playerSim)
              ? playerSim
              : [],
            meta,
            ...Object.fromEntries(stackData),
          });
        }
      } catch (e) {
        console.error(e);

        if (alive) {
          setErr(
            e?.message || "Failed to load NFL stack data."
          );
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  return { data, loading, err };
}

function stackPlayers(row, stackKey) {
  if (!row) return [];

  if (stackKey === "qb1") {
    return [
      { name: row.QB, role: "QB" },
      { name: row.Receiver, role: "REC" },
    ].filter((x) => x.name);
  }

  const count = stackKey === "qb2" ? 2 : 3;

  return [
    { name: row.QB, role: "QB" },
    ...Array.from({ length: count }, (_, i) => ({
      name: row[`Receiver${i + 1}`],
      role: "REC",
    })).filter((x) => x.name),
  ];
}

function buildTeamRows(data, stackKey, site, slate) {
  if (!data) return [];

  const vegasMap = new Map(
    (data.vegas || []).map((r) => [
      normTeam(r.team),
      r,
    ])
  );

  const siteIndex = buildSitePlayerIndex(data.siteIds);

  const grouped = new Map();

  for (const p of data.projections || []) {
    const team = normTeam(p.team);

    if (!team) continue;

    if (!grouped.has(team)) {
      grouped.set(team, []);
    }

    grouped.get(team).push(p);
  }

  const teamKey =
    STACK_OPTIONS.find((x) => x.key === stackKey)?.teamKey ||
    "qb2Team";

  const simByTeam = new Map();

  for (const r of data[teamKey] || []) {
    const team = normTeam(r.Team);

    const score =
      (n(r.DK_TopPassingStack_Pct) || 0) +
      (n(r.FD_TopPassingStack_Pct) || 0);

    const prev = simByTeam.get(team);

    const prevScore = prev
      ? (n(prev.DK_TopPassingStack_Pct) || 0) +
        (n(prev.FD_TopPassingStack_Pct) || 0)
      : -1;

    if (!prev || score > prevScore) {
      simByTeam.set(team, r);
    }
  }

  let rows = [...grouped.entries()].map(
    ([team, players]) => {
      const vg = vegasMap.get(team) || {};
      const sim = simByTeam.get(team) || {};

      const calc = (siteKey) => {
        const eligible = players
          .map((p) => {
            const slateInfo = getSlatePlayerInfo(
              siteIndex,
              siteKey,
              team,
              p.player,
              slate
            );

            if (!slateInfo) return null;

            return {
              player: p,
              salary: slateInfo.salary,
            };
          })
          .filter(Boolean);

        if (!eligible.length) {
          return {
            eligible: false,
            count: 0,
            salary: null,
            own: null,
            opt: null,
            rtg: null,
            total: null,
            QB: null,
            RB: null,
            WR: null,
            TE: null,
            DST: null,
          };
        }

        const activePlayers = eligible.map(
          (x) => x.player
        );

        // Exclude defense from team-average metrics.
        // DST still remains in position projections and total projection.
        const avgPlayers = activePlayers.filter(
          (p) => normPos(p.pos) !== "DST"
        );

        const avgEligible = eligible.filter(
          (x) => normPos(x.player.pos) !== "DST"
        );

        const posProj = Object.fromEntries(
          POSITIONS.map((pos) => [
            pos,
            sum(
              activePlayers
                .filter(
                  (p) => normPos(p.pos) === pos
                )
                .map(
                  (p) =>
                    p[`${siteKey}_proj`]
                )
            ),
          ])
        );

        return {
          eligible: true,
          count: eligible.length,

          // OFFENSE ONLY
          salary: mean(
            avgEligible.map((x) => x.salary)
          ),

          // OFFENSE ONLY
          own: mean(
            avgPlayers.map(
              (p) =>
                p[`${siteKey}_pown`]
            )
          ),

          // OFFENSE ONLY
          opt: mean(
            avgPlayers.map(
              (p) =>
                p[`${siteKey}_opt`]
            )
          ),

          // OFFENSE ONLY
          rtg: mean(
            avgPlayers.map(
              (p) =>
                p[`${siteKey}_rtg`]
            )
          ),

          // STILL INCLUDES DST
          total: sum(
            activePlayers.map(
              (p) =>
                p[`${siteKey}_proj`]
            )
          ),

          ...posProj,
        };
      };

      const dk = calc("dk");
      const fd = calc("fd");

      return {
        team,
        opp: normTeam(vg.opponent),
        field: normTeam(vg.field),
        overUnder: n(vg.over_under),
        spread: n(vg.spread),
        teamTotal: n(vg.team_total),

        dkEligible: dk.eligible,
        fdEligible: fd.eligible,

        dkSal: dk.salary,
        fdSal: fd.salary,

        dkOwn: dk.own,
        fdOwn: fd.own,

        dkOpt: dk.opt,
        fdOpt: fd.opt,

        dkRtg: dk.rtg,
        fdRtg: fd.rtg,

        dkQB: dk.QB,
        dkRB: dk.RB,
        dkWR: dk.WR,
        dkTE: dk.TE,
        dkDST: dk.DST,
        dkTotal: dk.total,

        fdQB: fd.QB,
        fdRB: fd.RB,
        fdWR: fd.WR,
        fdTE: fd.TE,
        fdDST: fd.DST,
        fdTotal: fd.total,

        dkStackRaw: n(
          sim.DK_TopPassingStack_Pct
        ),
        fdStackRaw: n(
          sim.FD_TopPassingStack_Pct
        ),

        dkStack: null,
        fdStack: null,

        qb: sim.QB || "",
      };
    }
  );

  rows = normalizePctPoints(
    rows,
    "dkStackRaw",
    "dkEligible",
    "dkStack"
  );

  rows = normalizePctPoints(
    rows,
    "fdStackRaw",
    "fdEligible",
    "fdStack"
  );

  return rows.filter((row) => {
    if (site === "dk") return row.dkEligible;
    if (site === "fd") return row.fdEligible;

    return row.dkEligible || row.fdEligible;
  });
}

function buildExactRows(data, stackKey, site, slate) {
  if (!data) return [];

  const actualSite =
    site === "fd" ? "fd" : "dk";

  const prefix = actualSite.toUpperCase();

  const vegasMap = new Map(
    (data.vegas || []).map((r) => [
      normTeam(r.team),
      r,
    ])
  );

  const projMap = new Map(
    (data.projections || []).map((r) => [
      playerKey(r.team, r.player),
      r,
    ])
  );

  const siteIndex =
    buildSitePlayerIndex(data.siteIds);

  const teamKey =
    STACK_OPTIONS.find((x) => x.key === stackKey)
      ?.teamKey || "qb2Team";

  const teamMap = new Map(
    (data[teamKey] || []).map((r) => [
      `${normTeam(r.Team)}|${normName(r.QB)}`,
      r,
    ])
  );

  const rows = (data[stackKey] || [])
    .map((raw, index) => {
      const team = normTeam(raw.Team);
      const vg = vegasMap.get(team) || {};

      const stack = stackPlayers(
        raw,
        stackKey
      );

      if (!stack.length) return null;

      const players = stack.map((p) => {
        const slateInfo =
          getSlatePlayerInfo(
            siteIndex,
            actualSite,
            team,
            p.name,
            slate
          );

        if (!slateInfo) return null;

        const proj =
          projMap.get(
            playerKey(team, p.name)
          );

        // Do not allow stale simulated stacks to survive when a player has
        // been removed from the current projections (injury/out/etc.).
        if (!proj) return null;

        const projValue = n(
          proj[`${actualSite}_proj`]
        );

        // A projection row with no usable projection for this site is also
        // treated as inactive for exact-stack purposes.
        if (projValue == null) return null;

        return {
          ...p,
          salary: slateInfo.salary,
          proj: projValue,
          own: n(
            proj[`${actualSite}_pown`]
          ),
        };
      });

      if (players.some((p) => !p)) {
        return null;
      }

      const t =
        teamMap.get(
          `${team}|${normName(raw.QB)}`
        ) || {};

      const recs = players
        .filter((p) => p.role !== "QB")
        .map((p) => p.name)
        .join(" + ");

      const salary = players.reduce(
        (acc, p) =>
          acc + (n(p.salary) || 0),
        0
      );

      const projVals = players
        .map((p) => n(p.proj))
        .filter((v) => v != null);

      const ownVals = players
        .map((p) => n(p.own))
        .filter((v) => v != null);

      return {
        id: `${stackKey}-${actualSite}-${slate}-${team}-${index}`,

        team,
        opp: normTeam(vg.opponent),
        overUnder: n(vg.over_under),
        spread: n(vg.spread),
        teamTotal: n(vg.team_total),

        qb: raw.QB,
        receivers: recs,

        salary,

        proj: projVals.length
          ? projVals.reduce(
              (a, b) => a + b,
              0
            )
          : null,

        ownSum:
          ownVals.length ===
          players.length
            ? ownVals.reduce(
                (a, b) => a + b,
                0
              )
            : null,

        comboPctRaw: n(
          raw[
            `${prefix}_TopStack_Pct`
          ]
        ),

        teamStackPctRaw: n(
          t[
            `${prefix}_TopPassingStack_Pct`
          ]
        ),

        comboPct: null,
        teamStackPct: null,

        stackMean: n(
          raw[
            `${prefix}_StackScore_Mean`
          ]
        ),

        stackP90: n(
          raw[
            `${prefix}_StackScore_P90`
          ]
        ),

        teamQbKey: `${team}|${normName(
          raw.QB
        )}`,
      };
    })
    .filter(Boolean);

  const comboTotal = rows.reduce(
    (acc, r) =>
      acc +
      ((n(r.comboPctRaw) || 0) > 0
        ? n(r.comboPctRaw) || 0
        : 0),
    0
  );

  const teamRaw = new Map();

  for (const r of rows) {
    const raw = n(r.teamStackPctRaw);

    if (
      raw != null &&
      raw > 0 &&
      !teamRaw.has(r.teamQbKey)
    ) {
      teamRaw.set(r.teamQbKey, raw);
    }
  }

  const teamTotal = [
    ...teamRaw.values(),
  ].reduce((a, b) => a + b, 0);

  return rows.map((r) => ({
    ...r,

    comboPct:
      comboTotal > 0 &&
      (n(r.comboPctRaw) || 0) > 0
        ? ((n(r.comboPctRaw) || 0) /
            comboTotal) *
          100
        : null,

    teamStackPct:
      teamTotal > 0 &&
      (teamRaw.get(r.teamQbKey) || 0) > 0
        ? ((teamRaw.get(r.teamQbKey) || 0) /
            teamTotal) *
          100
        : null,
  }));
}

function palette(isDark) {
  return {
    page: isDark
      ? "min-h-screen bg-slate-950 text-slate-100"
      : "min-h-screen bg-slate-100 text-slate-900",

    card: isDark
      ? "rounded-2xl border border-slate-800 bg-slate-900/95 shadow-sm"
      : "rounded-2xl border border-slate-200 bg-white",

    control: isDark
      ? "bg-slate-900 text-slate-300 hover:bg-slate-800"
      : "bg-slate-100 text-slate-600 hover:bg-slate-200",

    tabOn: isDark ? "text-white" : "text-slate-900",
    tabOff: isDark ? "text-slate-500 hover:text-slate-300" : "text-slate-500 hover:text-slate-800",

    active:
      "bg-sky-500 text-slate-950 shadow-sm",

    input: isDark
      ? "border-slate-700 bg-slate-950/80 text-slate-100 placeholder:text-slate-500"
      : "border-slate-300 bg-white text-slate-900",

    head: isDark
      ? "bg-slate-950 text-slate-200 border-slate-800"
      : "bg-slate-100 text-slate-700 border-slate-200",

    rowA: isDark
      ? "bg-slate-950"
      : "bg-white",

    rowB: isDark
      ? "bg-slate-900"
      : "bg-slate-50",

    border: isDark
      ? "border-slate-800"
      : "border-slate-200",
  };
}

function heatDirectionForKey(key) {
  if (
    [
      "overUnder",
      "teamTotal",

      "dkOpt",
      "dkRtg",
      "dkQB",
      "dkRB",
      "dkWR",
      "dkTE",
      "dkDST",
      "dkTotal",
      "dkStack",

      "fdOpt",
      "fdRtg",
      "fdQB",
      "fdRB",
      "fdWR",
      "fdTE",
      "fdDST",
      "fdTotal",
      "fdStack",

      "stackMean",
      "comboPct",
      "teamStackPct",
      "stackP90",
    ].includes(key)
  ) {
    return "higher";
  }

  if (
    [
      "dkSal",
      "fdSal",
      "salary",
      "dkOwn",
      "fdOwn",
      "ownSum",
    ].includes(key)
  ) {
    return "lower";
  }

  return null;
}

function heatTextColor(
  min,
  max,
  value,
  direction,
  isDark
) {
  const v = n(value);

  if (
    v == null ||
    min == null ||
    max == null ||
    min === max ||
    !direction
  ) {
    return null;
  }

  let t =
    (v - min) / (max - min);

  t = Math.max(
    0,
    Math.min(1, t)
  );

  if (direction === "lower") {
    t = 1 - t;
  }

  let hue;
  let saturation;
  let lightness;

  if (isDark) {
    if (t < 0.5) {
      const u = t / 0.5;

      hue = 0 + u * 60;
      saturation = 80;
      lightness = 62 + u * 10;
    } else {
      const u =
        (t - 0.5) / 0.5;

      hue = 60 + u * 60;
      saturation = 75;
      lightness = 72 - u * 8;
    }
  } else {
    if (t < 0.5) {
      const u = t / 0.5;

      hue = 0 + u * 60;
      saturation = 85;
      lightness = 42 + u * 6;
    } else {
      const u =
        (t - 0.5) / 0.5;

      hue = 60 + u * 60;
      saturation = 80;
      lightness = 38 - u * 4;
    }
  }

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
}

function teamColumns(
  site,
  stackLabel = "QB + 2"
) {
  const compactStack = String(
    stackLabel || "QB + 2"
  ).replace(/\s+/g, "");

  const base = [
    {
      key: "team",
      label: "Team",
      left: true,
    },
    {
      key: "opp",
      label: "Opp",
    },
    {
      key: "field",
      label: "Field",
    },
    {
      key: "overUnder",
      label: "Game Total",
      render: (r) =>
        fmt.num(r.overUnder),
    },
    {
      key: "spread",
      label: "Spread",
      render: (r) =>
        fmt.signed(r.spread),
    },
    {
      key: "teamTotal",
      label: "Team Total",
      render: (r) =>
        fmt.num(r.teamTotal),
    },
  ];

  const dk = [
    {
      key: "dkSal",
      label: "DK Avg Sal",
      render: (r) =>
        fmt.int(r.dkSal),
    },
    {
      key: "dkOwn",
      label: "DK Avg Own%",
      render: (r) =>
        fmt.pctFraction(r.dkOwn),
    },
    {
      key: "dkOpt",
      label: "DK Avg Opt%",
      render: (r) =>
        fmt.pctFraction(r.dkOpt),
    },
    {
      key: "dkRtg",
      label: "DK Avg Rtg",
      render: (r) =>
        fmt.num(r.dkRtg),
    },
    {
      key: "dkQB",
      label: "DK QB Proj",
      render: (r) =>
        fmt.num(r.dkQB),
    },
    {
      key: "dkRB",
      label: "DK RB Proj",
      render: (r) =>
        fmt.num(r.dkRB),
    },
    {
      key: "dkWR",
      label: "DK WR Proj",
      render: (r) =>
        fmt.num(r.dkWR),
    },
    {
      key: "dkTE",
      label: "DK TE Proj",
      render: (r) =>
        fmt.num(r.dkTE),
    },
    {
      key: "dkDST",
      label: "DK DST Proj",
      render: (r) =>
        fmt.num(r.dkDST),
    },
    {
      key: "dkTotal",
      label: "DK Team Proj",
      render: (r) =>
        fmt.num(r.dkTotal),
    },
    {
      key: "dkStack",
      label: `DK ${compactStack} Win%`,
      render: (r) =>
        fmt.pctPoints(r.dkStack),
      accent: true,
    },
  ];

  const fd = [
    {
      key: "fdSal",
      label: "FD Avg Sal",
      render: (r) =>
        fmt.int(r.fdSal),
    },
    {
      key: "fdOwn",
      label: "FD Avg Own%",
      render: (r) =>
        fmt.pctFraction(r.fdOwn),
    },
    {
      key: "fdOpt",
      label: "FD Avg Opt%",
      render: (r) =>
        fmt.pctFraction(r.fdOpt),
    },
    {
      key: "fdRtg",
      label: "FD Avg Rtg",
      render: (r) =>
        fmt.num(r.fdRtg),
    },
    {
      key: "fdQB",
      label: "FD QB Proj",
      render: (r) =>
        fmt.num(r.fdQB),
    },
    {
      key: "fdRB",
      label: "FD RB Proj",
      render: (r) =>
        fmt.num(r.fdRB),
    },
    {
      key: "fdWR",
      label: "FD WR Proj",
      render: (r) =>
        fmt.num(r.fdWR),
    },
    {
      key: "fdTE",
      label: "FD TE Proj",
      render: (r) =>
        fmt.num(r.fdTE),
    },
    {
      key: "fdDST",
      label: "FD DST Proj",
      render: (r) =>
        fmt.num(r.fdDST),
    },
    {
      key: "fdTotal",
      label: "FD Team Proj",
      render: (r) =>
        fmt.num(r.fdTotal),
    },
    {
      key: "fdStack",
      label: `FD ${compactStack} Win%`,
      render: (r) =>
        fmt.pctPoints(r.fdStack),
      accent: true,
    },
  ];

  if (site === "dk") {
    return [...base, ...dk];
  }

  if (site === "fd") {
    return [...base, ...fd];
  }

  return [
    ...base,
    ...dk,
    ...fd,
  ];
}

const EXACT_COLUMNS = [
  {
    key: "team",
    label: "Team",
    left: true,
  },
  {
    key: "opp",
    label: "Opp",
  },
  {
    key: "overUnder",
    label: "O/U",
    render: (r) =>
      fmt.num(r.overUnder),
  },
  {
    key: "spread",
    label: "Spread",
    render: (r) =>
      fmt.signed(r.spread),
  },
  {
    key: "teamTotal",
    label: "Imp Tot",
    render: (r) =>
      fmt.num(r.teamTotal),
  },
  {
    key: "qb",
    label: "QB",
    left: true,
  },
  {
    key: "receivers",
    label: "Stack",
    left: true,
  },
  {
    key: "salary",
    label: "Salary",
    render: (r) => {
      const x = n(r.salary);

      return x == null || x <= 0
        ? ""
        : fmt.money(x);
    },
  },
  {
    key: "stackMean",
    label: "Stack Proj",
    render: (r) => {
      const x = n(r.stackMean);

      return x == null
        ? ""
        : fmt.num(x);
    },
  },
  {
    key: "stackP90",
    label: "Ceiling",
    render: (r) =>
      fmt.num(r.stackP90),
  },
  {
    key: "ownSum",
    label: "Own Sum",
    render: (r) => {
      const x = n(r.ownSum);

      return x == null || x <= 0
        ? ""
        : fmt.pctFraction(x);
    },
  },
  {
    key: "comboPct",
    label: "Exact Win%",
    render: (r) =>
      fmt.pctPoints(r.comboPct),
    accent: true,
  },
  {
    key: "teamStackPct",
    label: "Team Win%",
    render: (r) =>
      fmt.pctPoints(r.teamStackPct),
    accent: true,
  },
];

function sortData(rows, sort) {
  const dir =
    sort.dir === "asc" ? 1 : -1;

  return [...rows].sort(
    (a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];

      let an = n(av);
      let bn = n(bv);

      if (
        [
          "salary",
          "stackMean",
          "ownSum",
        ].includes(sort.key)
      ) {
        if (
          an != null &&
          an <= 0
        ) {
          an = null;
        }

        if (
          bn != null &&
          bn <= 0
        ) {
          bn = null;
        }
      }

      if (
        an != null ||
        bn != null
      ) {
        if (an == null) {
          return 1;
        }

        if (bn == null) {
          return -1;
        }

        return (
          (an - bn) * dir
        );
      }

      return (
        String(av ?? "").localeCompare(
          String(bv ?? "")
        ) * dir
      );
    }
  );
}

function DataTable({
  rows,
  columns,
  sort,
  setSort,
  isDark,
}) {
  const p = palette(isDark);

  const toggleSort = (key) => {
    setSort((prev) =>
      prev.key === key
        ? {
            key,
            dir:
              prev.dir === "desc"
                ? "asc"
                : "desc",
          }
        : {
            key,
            dir: "desc",
          }
    );
  };

  const heatRanges = useMemo(() => {
    const out = {};

    for (const c of columns) {
      const direction =
        heatDirectionForKey(c.key);

      if (!direction) continue;

      let vals = rows
        .map((r) => n(r[c.key]))
        .filter((v) => v != null);

      if (
        [
          "dkSal",
          "fdSal",
          "salary",
          "stackMean",
          "ownSum",
        ].includes(c.key)
      ) {
        vals = vals.filter(
          (v) => v > 0
        );
      }

      if (!vals.length) {
        continue;
      }

      out[c.key] = {
        min: Math.min(...vals),
        max: Math.max(...vals),
        direction,
      };
    }

    return out;
  }, [rows, columns]);

  return (
    <div
      className={`${p.card} overflow-hidden`}
    >
      <div className="max-h-[560px] overflow-auto">
        <table className="w-full min-w-max border-collapse text-[10px] md:text-[11px]">
          <thead className="sticky top-0 z-20">
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  onClick={() =>
                    toggleSort(c.key)
                  }
                  className={`cursor-pointer whitespace-nowrap border-b px-1.5 py-1.5 font-semibold ${
                    c.left
                      ? "text-left"
                      : "text-center"
                  } ${p.head}`}
                >
                  <span className="inline-flex items-center gap-1">
                    {c.label}

                    {sort.key ===
                    c.key ? (
                      <span className="text-[#72b5e7]">
                        {sort.dir ===
                        "desc"
                          ? "▼"
                          : "▲"}
                      </span>
                    ) : null}
                  </span>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.map((r, i) => (
              <tr
                key={
                  r.id ||
                  `${r.team}-${i}`
                }
                className={`border-b ${p.border} ${
                  i % 2
                    ? p.rowB
                    : p.rowA
                }`}
              >
                {columns.map((c) => {
                  const range =
                    heatRanges[c.key];

                  const rawHeatValue =
                    n(r[c.key]);

                  const skipHeat =
                    [
                      "dkSal",
                      "fdSal",
                      "salary",
                      "stackMean",
                      "ownSum",
                    ].includes(c.key) &&
                    (rawHeatValue ==
                      null ||
                      rawHeatValue <=
                        0);

                  const color =
                    range &&
                    !skipHeat
                      ? heatTextColor(
                          range.min,
                          range.max,
                          rawHeatValue,
                          range.direction,
                          isDark
                        )
                      : null;

                  return (
                    <td
                      key={c.key}
                      className={`whitespace-nowrap px-1.5 py-1.5 ${
                        c.left
                          ? "text-left"
                          : "text-center"
                      }`}
                      style={
                        color
                          ? {
                              color,
                              fontWeight: 700,
                            }
                          : undefined
                      }
                    >
                      {c.key ===
                      "team" ? (
                        <div className="flex items-center gap-2 font-bold">
                          <img
                            src={teamLogo(
                              r.team
                            )}
                            alt=""
                            className="h-5 w-5 object-contain"
                            onError={(
                              e
                            ) =>
                              (e.currentTarget.style.display =
                                "none")
                            }
                          />

                          {r.team}
                        </div>
                      ) : c.key ===
                        "receivers" ? (
                        <div className="max-w-[360px] truncate font-medium">
                          {
                            r.receivers
                          }
                        </div>
                      ) : c.render ? (
                        c.render(r)
                      ) : (
                        r[c.key] ??
                        "—"
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>

        {!rows.length ? (
          <div className="p-8 text-center text-sm text-slate-500">
            No rows match.
          </div>
        ) : null}
      </div>
    </div>
  );
}

const CHARTS = {
  projection_implied: {
    label:
      "Team Projection vs Implied Total",
    x: {
      key: "teamTotal",
      label:
        "Implied Team Total",
    },
    dk: {
      key: "dkTotal",
      label:
        "DK Team Projection",
    },
    fd: {
      key: "fdTotal",
      label:
        "FD Team Projection",
    },
  },

  stack_implied: {
    label:
      "Stack Win% vs Implied Total",
    x: {
      key: "teamTotal",
      label:
        "Implied Team Total",
    },
    dk: {
      key: "dkStack",
      label:
        "DK Stack Win %",
      pct: true,
      pctMode: "points",
    },
    fd: {
      key: "fdStack",
      label:
        "FD Stack Win %",
      pct: true,
      pctMode: "points",
    },
  },

  projection_own: {
    label:
      "Team Projection vs pOWN%",
    xdk: {
      key: "dkOwn",
      label: "DK pOWN %",
      pct: true,
    },
    xfd: {
      key: "fdOwn",
      label: "FD pOWN %",
      pct: true,
    },
    dk: {
      key: "dkTotal",
      label:
        "DK Team Projection",
    },
    fd: {
      key: "fdTotal",
      label:
        "FD Team Projection",
    },
  },

  stack_own: {
    label:
      "Stack Win% vs pOWN%",
    xdk: {
      key: "dkOwn",
      label: "DK pOWN %",
      pct: true,
    },
    xfd: {
      key: "fdOwn",
      label: "FD pOWN %",
      pct: true,
    },
    dk: {
      key: "dkStack",
      label:
        "DK Stack Win %",
      pct: true,
      pctMode: "points",
    },
    fd: {
      key: "fdStack",
      label:
        "FD Stack Win %",
      pct: true,
      pctMode: "points",
    },
  },

  wr_total: {
    label:
      "WR Projection vs Team Projection",
    xdk: {
      key: "dkTotal",
      label:
        "DK Team Projection",
    },
    xfd: {
      key: "fdTotal",
      label:
        "FD Team Projection",
    },
    dk: {
      key: "dkWR",
      label:
        "DK WR Projection",
    },
    fd: {
      key: "fdWR",
      label:
        "FD WR Projection",
    },
  },

  qb_total: {
    label:
      "QB Projection vs Team Projection",
    xdk: {
      key: "dkTotal",
      label:
        "DK Team Projection",
    },
    xfd: {
      key: "fdTotal",
      label:
        "FD Team Projection",
    },
    dk: {
      key: "dkQB",
      label:
        "DK QB Projection",
    },
    fd: {
      key: "fdQB",
      label:
        "FD QB Projection",
    },
  },
};

function LogoDot({
  cx,
  cy,
  payload,
}) {
  if (cx == null || cy == null) {
    return null;
  }

  const size = 28;

  return (
    <image
      href={teamLogo(
        payload?.row?.team
      )}
      x={cx - size / 2}
      y={cy - size / 2}
      width={size}
      height={size}
    />
  );
}

function ChartTooltip({
  active,
  payload,
  xMetric,
  yMetric,
  site,
}) {
  if (
    !active ||
    !payload?.length
  ) {
    return null;
  }

  const d =
    payload[0]?.payload;

  const r = d?.row;

  if (!r) return null;

  const display = (m, v) =>
    m?.pct
      ? `${Number(v).toFixed(
          1
        )}%`
      : fmt.num(v);

  return (
    <div className="rounded-xl border border-slate-700 bg-[#080d19]/95 px-3 py-2 text-xs text-slate-100 shadow-2xl">
      <div className="flex items-center gap-2 font-bold">
        <img
          src={teamLogo(r.team)}
          alt=""
          className="h-5 w-5 object-contain"
        />

        {r.team} vs {r.opp}
      </div>

      <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
        <span className="text-slate-400">
          {xMetric.label}
        </span>

        <span className="text-right">
          {display(
            xMetric,
            d.x
          )}
        </span>

        <span className="text-slate-400">
          {yMetric.label}
        </span>

        <span className="text-right">
          {display(
            yMetric,
            d.y
          )}
        </span>
      </div>

      <div className="mt-2 text-[10px] uppercase tracking-wide text-slate-500">
        {site.toUpperCase()}
      </div>
    </div>
  );
}

function median(values) {
  const clean = values
    .map((v) => n(v))
    .filter((v) => v != null)
    .sort((a, b) => a - b);

  if (!clean.length) {
    return null;
  }

  const mid = Math.floor(
    clean.length / 2
  );

  return clean.length % 2
    ? clean[mid]
    : (clean[mid - 1] +
        clean[mid]) /
        2;
}

function TeamInsights({
  rows,
  site,
  isDark,
}) {
  const [chartKey, setChartKey] =
    useState(
      "projection_implied"
    );

  const actualSite =
    site === "fd"
      ? "fd"
      : "dk";

  const chart =
    CHARTS[chartKey];

  const xMetric =
    chart[`x${actualSite}`] ||
    chart.x;

  const yMetric =
    chart[actualSite];

  const chartRows = useMemo(
    () =>
      rows
        .map((r) => ({
          x: xMetric?.pct
            ? xMetric.pctMode ===
              "points"
              ? pctPointValue(
                  r[
                    xMetric.key
                  ]
                )
              : pctFraction(
                  r[
                    xMetric.key
                  ]
                )
            : n(
                r[
                  xMetric.key
                ]
              ),

          y: yMetric?.pct
            ? yMetric.pctMode ===
              "points"
              ? pctPointValue(
                  r[
                    yMetric.key
                  ]
                )
              : pctFraction(
                  r[
                    yMetric.key
                  ]
                )
            : n(
                r[
                  yMetric.key
                ]
              ),

          row: r,
        }))
        .filter(
          (r) =>
            r.x != null &&
            r.y != null
        ),
    [rows, xMetric, yMetric]
  );

  const xMedian = useMemo(
    () =>
      median(
        chartRows.map(
          (r) => r.x
        )
      ),
    [chartRows]
  );

  const yMedian = useMemo(
    () =>
      median(
        chartRows.map(
          (r) => r.y
        )
      ),
    [chartRows]
  );

  const p = palette(isDark);

  return (
    <div
      className={`${p.card} p-4`}
    >
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-lg font-extrabold">
            Stack Insights
          </h2>

          <p className="mt-1 text-xs text-slate-500">
            One logo per team.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {site === "both" ? (
            <div className={`rounded-md px-3 py-2 text-[11px] ${p.control}`}>
              Chart uses DK in
              Both mode
            </div>
          ) : null}

          <select
            value={chartKey}
            onChange={(e) =>
              setChartKey(
                e.target.value
              )
            }
            className={`h-8 min-w-0 max-w-full rounded-md border px-3 text-xs font-semibold outline-none ${p.input}`}
          >
            {Object.entries(
              CHARTS
            ).map(
              ([key, x]) => (
                <option
                  key={key}
                  value={key}
                >
                  {x.label}
                </option>
              )
            )}
          </select>
        </div>
      </div>

      <div className="h-[400px] w-full">
        <ResponsiveContainer
          width="100%"
          height="100%"
        >
          <ScatterChart
            margin={{
              top: 20,
              right: 30,
              bottom: 35,
              left: 25,
            }}
          >
            <XAxis
              type="number"
              dataKey="x"
              domain={[
                (dataMin) =>
                  Math.floor(
                    (dataMin -
                      0.75) *
                      2
                  ) / 2,

                (dataMax) =>
                  Math.ceil(
                    (dataMax +
                      0.75) *
                      2
                  ) / 2,
              ]}
              allowDataOverflow={
                false
              }
              tickFormatter={(
                v
              ) => {
                const x =
                  Number(v);

                return Number.isInteger(
                  x
                )
                  ? String(x)
                  : x.toFixed(1);
              }}
              tick={{
                fontSize: 11,
                fill: isDark
                  ? "#9ab1ce"
                  : "#64748b",
              }}
              tickLine={false}
              axisLine={{
                stroke: isDark
                  ? "#334155"
                  : "#cbd5e1",
              }}
              label={{
                value:
                  xMetric.label,
                position:
                  "insideBottom",
                offset: -20,
                fill: isDark
                  ? "#9ab1ce"
                  : "#64748b",
                fontSize: 11,
              }}
            />

            <YAxis
              type="number"
              dataKey="y"
              domain={[
                (dataMin) =>
                  Math.floor(
                    (dataMin - 3) /
                      5
                  ) * 5,

                (dataMax) =>
                  Math.ceil(
                    (dataMax + 3) /
                      5
                  ) * 5,
              ]}
              allowDataOverflow={
                false
              }
              width={65}
              tickFormatter={(
                v
              ) => {
                const x =
                  Number(v);

                return Number.isInteger(
                  x
                )
                  ? String(x)
                  : x.toFixed(1);
              }}
              tick={{
                fontSize: 11,
                fill: isDark
                  ? "#9ab1ce"
                  : "#64748b",
              }}
              tickLine={false}
              axisLine={{
                stroke: isDark
                  ? "#334155"
                  : "#cbd5e1",
              }}
              label={{
                value:
                  yMetric.label,
                angle: -90,
                position:
                  "insideLeft",
                fill: isDark
                  ? "#9ab1ce"
                  : "#64748b",
                fontSize: 11,
              }}
            />

            {xMedian != null ? (
              <ReferenceLine
                x={xMedian}
                stroke={
                  isDark
                    ? "#7f8ea8"
                    : "#64748b"
                }
                strokeDasharray="6 5"
                strokeWidth={
                  1.25
                }
              />
            ) : null}

            {yMedian != null ? (
              <ReferenceLine
                y={yMedian}
                stroke={
                  isDark
                    ? "#7f8ea8"
                    : "#64748b"
                }
                strokeDasharray="6 5"
                strokeWidth={
                  1.25
                }
              />
            ) : null}

            <Tooltip
              cursor={{
                strokeDasharray:
                  "3 3",
                stroke: isDark
                  ? "#475569"
                  : "#94a3b8",
              }}
              content={
                <ChartTooltip
                  xMetric={
                    xMetric
                  }
                  yMetric={
                    yMetric
                  }
                  site={
                    actualSite
                  }
                />
              }
            />

            <Scatter
              data={chartRows}
              shape={<LogoDot />}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function escapeCSV(v) {
  const s = String(
    v ?? ""
  );

  return /[",\n\r]/.test(s)
    ? `"${s.replace(
        /"/g,
        '""'
      )}"`
    : s;
}

function downloadCSV(
  rows,
  mode,
  site,
  stackLabel = "QB + 2"
) {
  let cols;

  if (mode === "teams") {
    cols = teamColumns(
      site,
      stackLabel
    ).map((c) => [
      c.label,
      (r) => r[c.key],
    ]);
  } else {
    cols =
      EXACT_COLUMNS.map(
        (c) => [
          c.label,
          (r) => r[c.key],
        ]
      );
  }

  const csv = [
    cols
      .map(([x]) =>
        escapeCSV(x)
      )
      .join(","),

    ...rows.map((r) =>
      cols
        .map(([, get]) =>
          escapeCSV(
            get(r)
          )
        )
        .join(",")
    ),
  ].join("\n");

  const blob = new Blob(
    [csv],
    {
      type: "text/csv;charset=utf-8;",
    }
  );

  const url =
    URL.createObjectURL(blob);

  const a =
    document.createElement(
      "a"
    );

  a.href = url;

  a.download =
    mode === "teams"
      ? "nfl_team_stacks.csv"
      : "nfl_exact_stacks.csv";

  a.click();

  URL.revokeObjectURL(url);
}

function Seg({
  active,
  onClick,
  children,
  p,
}) {
  return (
    <button
      onClick={onClick}
      type="button"
      aria-pressed={active}
      className={`relative inline-flex h-9 items-center whitespace-nowrap pb-2 text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 ${active ? p.tabOn + " after:absolute after:left-0 after:right-0 after:-bottom-px after:h-0.5 after:rounded-full after:bg-sky-400" : p.tabOff}`}
    >
      {children}
    </button>
  );
}

function Divider() {
  return (
    <div className="mx-1 hidden h-8 w-px bg-slate-800 sm:block" />
  );
}

export default function NflStacks() {
  const {
    data,
    loading,
    err,
  } = useStackData();

  const [theme, setTheme] =
    useState("dark");

  const [site, setSite] =
    useState("both");

  const [slate, setSlate] =
    useState("all");

  const [mode, setMode] =
    useState("teams");

  const [
    stackKey,
    setStackKey,
  ] = useState("qb2");

  const [view, setView] =
    useState("both");

  const [query, setQuery] =
    useState("");

  const [
    teamSort,
    setTeamSort,
  ] = useState({
    key: "dkTotal",
    dir: "desc",
  });

  const [
    exactSort,
    setExactSort,
  ] = useState({
    key: "comboPct",
    dir: "desc",
  });

  const isDark =
    theme === "dark";

  const p = palette(isDark);

  const stackOption =
    STACK_OPTIONS.find(
      (x) =>
        x.key === stackKey
    ) || STACK_OPTIONS[1];

  const visibleSiteOptions =
    mode === "exact"
      ? SITE_OPTIONS.filter(
          (x) =>
            x.key !== "both"
        )
      : SITE_OPTIONS;

  const teamRows = useMemo(
    () =>
      buildTeamRows(
        data,
        stackKey,
        site,
        slate
      ),
    [
      data,
      stackKey,
      site,
      slate,
    ]
  );

  const exactRows = useMemo(
    () =>
      buildExactRows(
        data,
        stackKey,
        site,
        slate
      ),
    [
      data,
      stackKey,
      site,
      slate,
    ]
  );

  const filterRows = (rows) => {
    const q = query
      .trim()
      .toLowerCase();

    if (!q) {
      return rows;
    }

    return rows.filter((r) =>
      Object.values(r)
        .filter(
          (v) =>
            typeof v ===
            "string"
        )
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  };

  const filteredTeams =
    useMemo(
      () =>
        filterRows(teamRows),
      [teamRows, query]
    );

  const filteredExact =
    useMemo(
      () =>
        filterRows(exactRows),
      [exactRows, query]
    );

  const sortedTeams =
    useMemo(
      () =>
        sortData(
          filteredTeams,
          teamSort
        ),
      [
        filteredTeams,
        teamSort,
      ]
    );

  const sortedExact =
    useMemo(
      () =>
        sortData(
          filteredExact,
          exactSort
        ),
      [
        filteredExact,
        exactSort,
      ]
    );

  const updated = useMemo(
    () => {
      const raw =
        data?.meta
          ?.last_updated ||
        data?.meta?.tags
          ?.last_updated ||
        data?.meta?.artifacts?.find(
          (x) =>
            String(
              x?.path || ""
            ).endsWith(
              "projections.json"
            )
        )?.last_updated;

      if (!raw) return null;

      const d =
        new Date(raw);

      return Number.isNaN(
        d.getTime()
      )
        ? null
        : d.toLocaleString(
            undefined,
            {
              month:
                "numeric",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
            }
          );
    },
    [data]
  );

  const visibleRows =
    mode === "teams"
      ? sortedTeams
      : sortedExact;

  return (
    <div className={p.page}>
      <div className="w-full px-4 md:px-6 py-5">
        <header className="mb-3 flex flex-wrap items-center justify-between gap-x-5 gap-y-3">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
            <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight">NFL — Stacks</h1>
            {updated && <span className={"text-xs italic " + (isDark ? "text-slate-400" : "text-slate-600")}>Updated: {updated}</span>}
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-3">
            <div className={"inline-flex items-center rounded-full px-1 py-1 text-[11px] ring-1 " + (isDark ? "bg-slate-900/90 ring-slate-700/80" : "bg-white ring-slate-300")}>
              {["dark", "light"].map((value) => <button key={value} type="button" aria-pressed={theme === value} onClick={() => setTheme(value)} className={"rounded-full px-3 py-1 font-medium transition " + (theme === value ? "bg-sky-500 text-slate-950 shadow-sm" : "text-slate-500")}>{value === "dark" ? "Dark" : "Light"}</button>)}
            </div>
            <button
              onClick={() =>
                downloadCSV(
                  visibleRows,
                  mode,
                  site,
                  stackOption.label
                )
              }
              className="h-8 rounded-md bg-sky-500 px-3 text-xs font-semibold text-slate-950 hover:bg-sky-400"
            >
              Export CSV
            </button>
          </div>
        </header>

        <div
          role="region" aria-label="Stack controls" className={`${p.card} mb-3 p-3`}
        >
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
              <div className={"inline-flex items-center gap-5 border-b " + p.border}>
              <Seg
                active={
                  mode ===
                  "teams"
                }
                onClick={() =>
                  setMode(
                    "teams"
                  )
                }
                p={p}
              >
                Team Summary
              </Seg>

              <Seg
                active={
                  mode ===
                  "exact"
                }
                onClick={() => {
                  setMode(
                    "exact"
                  );

                  if (
                    site ===
                    "both"
                  ) {
                    setSite(
                      "dk"
                    );
                  }
                }}
                p={p}
              >
                Exact Stacks
              </Seg>

              </div>
              <div className={"inline-flex items-center gap-5 border-b " + p.border}>

              {STACK_OPTIONS.map(
                (x) => (
                  <Seg
                    key={
                      x.key
                    }
                    active={
                      stackKey ===
                      x.key
                    }
                    onClick={() =>
                      setStackKey(
                        x.key
                      )
                    }
                    p={p}
                  >
                    {x.label}
                  </Seg>
                )
              )}

              </div>
              <div className={"inline-flex items-center gap-5 border-b " + p.border}>

              {visibleSiteOptions.map(
                (x) => (
                  <button
                    key={
                      x.key
                    }
                    onClick={() => {
                      setSite(
                        x.key
                      );

                      if (
                        mode ===
                        "teams"
                      ) {
                        setTeamSort(
                          {
                            key:
                              x.key ===
                              "fd"
                                ? "fdTotal"
                                : "dkTotal",
                            dir:
                              "desc",
                          }
                        );
                      }
                    }}
                    type="button"
                    aria-pressed={site === x.key}
                    className={`relative inline-flex h-9 items-center gap-2 whitespace-nowrap pb-2 text-xs font-semibold transition ${site === x.key ? p.tabOn + " after:absolute after:left-0 after:right-0 after:-bottom-px after:h-0.5 after:bg-sky-400" : p.tabOff}`}
                  >
                    {x.logo ? (
                      <img
                        src={
                          x.logo
                        }
                        alt=""
                        className="h-4 w-4 object-contain"
                      />
                    ) : null}

                    {x.label}
                  </button>
                )
              )}

              </div>
              <div className={"inline-flex items-center gap-5 border-b " + p.border}>

              {SLATE_OPTIONS.map(
                (x) => (
                  <Seg
                    key={
                      x.key
                    }
                    active={
                      slate ===
                      x.key
                    }
                    onClick={() =>
                      setSlate(
                        x.key
                      )
                    }
                    p={p}
                  >
                    {x.label}
                  </Seg>
                )
              )}
              </div>
            </div>

            <div className="flex min-w-0 flex-wrap items-center gap-3">
              <input
                value={query}
                onChange={(e) =>
                  setQuery(
                    e.target
                      .value
                  )
                }
                placeholder={
                  mode ===
                  "teams"
                    ? "Search team / opponent..."
                    : "Search team, QB, receiver..."
                }
                className={`h-8 min-w-0 w-full sm:w-72 rounded-md border px-3 text-xs outline-none ${p.input}`}
              />

              <div className={"inline-flex items-center gap-5 border-b " + p.border}>
                {[
                  "table",
                  "insights",
                  "both",
                ].map((x) => (
                  <button
                    key={x}
                    onClick={() =>
                      setView(x)
                    }
                    type="button"
                    aria-pressed={view === x}
                    className={`relative h-8 pb-2 text-xs font-semibold capitalize transition ${view === x ? p.tabOn + " after:absolute after:left-0 after:right-0 after:-bottom-px after:h-0.5 after:bg-sky-400" : p.tabOff}`}
                  >
                    {x}
                  </button>
                ))}
              </div>
            </div>
          </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
              <span>
                {visibleRows.length.toLocaleString()}{" "}
                {mode ===
                "teams"
                  ? "teams"
                  : "exact stacks"}
              </span>

              <span>•</span>

              <span>
                {
                  stackOption.label
                }
              </span>

              <span>•</span>

              <span>
                {site.toUpperCase()}
              </span>

              <span>•</span>

              <span>
                {SLATE_OPTIONS.find(
                  (x) =>
                    x.key ===
                    slate
                )?.label ||
                  "All"}
              </span>


            </div>
        </div>

        {loading ? (
          <div
            className={`${p.card} p-10 text-center text-sm text-slate-500`}
          >
            Loading stack
            data…
          </div>
        ) : err ? (
          <div
            className={`${p.card} p-6 text-sm text-red-400`}
          >
            {err}
          </div>
        ) : (
          <>
            {(view ===
              "table" ||
              view ===
                "both") &&
              (mode ===
              "teams" ? (
                <DataTable
                  rows={
                    sortedTeams
                  }
                  columns={teamColumns(
                    site,
                    stackOption.label
                  )}
                  sort={
                    teamSort
                  }
                  setSort={
                    setTeamSort
                  }
                  isDark={
                    isDark
                  }
                />
              ) : (
                <DataTable
                  rows={
                    sortedExact
                  }
                  columns={
                    EXACT_COLUMNS
                  }
                  sort={
                    exactSort
                  }
                  setSort={
                    setExactSort
                  }
                  isDark={
                    isDark
                  }
                />
              ))}

            {(view ===
              "insights" ||
              view ===
                "both") && (
              <div
                className={
                  view ===
                  "both"
                    ? "mt-4"
                    : ""
                }
              >
                <TeamInsights
                  rows={
                    filteredTeams
                  }
                  site={site}
                  isDark={
                    isDark
                  }
                />
              </div>
            )}
          </>
        )}

        <div className="mt-2 px-2 text-[9px] leading-relaxed text-slate-600">
          Site/slate eligibility
          comes from site_ids.json.
          Team Summary only includes
          players posted on the
          selected site/slate. Average
          salary, ownership, optimal
          percentage, and rating are
          calculated from offensive
          players only and exclude
          DST. Team and positional
          projections still include
          DST. The selected
          QB+1/QB+2/QB+3 team Win% is
          re-normalized across eligible
          teams. Exact Stacks require
          every stack player to be
          posted on the selected
          site/slate; invalid stacks
          are removed before Exact
          Win% and Team Win% are
          re-normalized.
        </div>
      </div>
    </div>
  );
}